import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { Button, Card } from '../components/common';
import { verifyEmail } from '../services/api';
import MouseBubble from '../components/common/MouseBubble';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please register again.');
        return;
      }

      try {
        await verifyEmail(token);
        setStatus('success');
        setMessage('Your email has been verified successfully!');
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.error || 'Verification failed. The link may have expired.');
      }
    };

    verify();
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <Loader className="w-12 h-12 text-accent dark:text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-400 dark:text-gray-500">Verifying your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300 py-12 px-4">
      <Card className="w-full max-w-md p-8 text-center">
        {status === 'success' ? (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
              Email Verified!
            </h1>
            <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
              {message}
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Sign In
            </Button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
              Verification Failed
            </h1>
            <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
              {message}
            </p>
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/register')}
                className="w-full"
              >
                Create New Account
              </Button>
              <Link to="/" className="block">
                <Button variant="outline" size="lg" className="w-full">
                  Back to Home
                </Button>
              </Link>
            </div>
          </>
        )}
      </Card>
      <MouseBubble />
    </div>
  );
};

export default VerifyEmailPage;

